import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, Switch, FormControlLabel, Menu, MenuItem, useTheme, useMediaQuery, Chip, Checkbox, Tooltip, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExpandMore as ExpandMoreIcon, KeyboardArrowDown } from '@mui/icons-material';
import { MagicStar, Task, UserOctagon, Warning2, Like1, Cake, CalendarTick } from 'iconsax-react';
import { Icon } from '@iconify/react';
import { useForm, Controller } from 'react-hook-form';
import { ControlledModernHCheckboxAutocomplete } from '@ui/components/common/Autocomplete';
import HCheckboxAutoComplete from '@ui/components/common/Autocomplete/HCheckboxAutocomplete';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getNotificationPreferencesByModuleAsync, createNotificationPreferenceAsync, updateNotificationPreferenceAsync } from '@application/features/notificationPreferences/notificationPreferencesThunk';
import { NotificationModule } from '@application/enums/notification-module-enum';
import { TaskNotificationType, ActivityNotificationType } from '@application/enums/notification-type-enum';
import { mapPreferencesToCategories, parseTimingString } from './notificationPreferencesMapper';
import { PriorityEnum } from '@application/enums/priority-enum';
import { getModuleCompaniesAsync, updateModuleCompaniesAsync } from '@application/features/notificationPreferences/notificationPreferencesThunk';
import { setPreferencesByModule } from '@application/features/notificationPreferences/notificationPreferencesSlice';
import { EventNotificationType } from '@application/enums/notification-type-enum';
const getCompanyRegionGroups = (companies) => {
    const regionGroups = {};
    companies.forEach((company) => {
        const regionLabel = company.regionLabel || 'Other';
        if (!regionGroups[regionLabel]) {
            regionGroups[regionLabel] = [];
        }
        regionGroups[regionLabel].push(company.value);
    });
    return regionGroups;
};
const NotificationsTab = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchValue] = useState('');
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(1200));
    const isMobileScreen = useMediaQuery(theme.breakpoints.down(1024));
    const [expanded, setExpanded] = useState(['events', 'tasks', 'activities']);
    const [peopleMenuAnchor, setPeopleMenuAnchor] = useState(null);
    const [selectedPeopleOptions, setSelectedPeopleOptions] = useState([]);
    const [companyError, setCompanyError] = useState(undefined);
    // Function to get notification description based on type and priority
    const getNotificationDescription = (notificationType, taskPriority) => {
        if (!notificationType)
            return '';
        // Event notifications
        if (notificationType === EventNotificationType.EventCreated) {
            return t('notificationDescriptions.EventCreated');
        }
        if (notificationType === EventNotificationType.EventDeadline) {
            return t('notificationDescriptions.EventDeadline');
        }
        if (notificationType === EventNotificationType.EventRescheduled) {
            return t('notificationDescriptions.EventRescheduled');
        }
        // Task notifications
        if (notificationType === TaskNotificationType.TaskAssigned) {
            return t('notificationDescriptions.TaskAssigned');
        }
        if (notificationType === TaskNotificationType.TaskDeadlineApproaching) {
            if (taskPriority === PriorityEnum.High) {
                return t('notificationDescriptions.TaskDeadlineApproaching.High');
            }
            if (taskPriority === PriorityEnum.Medium) {
                return t('notificationDescriptions.TaskDeadlineApproaching.Medium');
            }
            if (taskPriority === PriorityEnum.Low) {
                return t('notificationDescriptions.TaskDeadlineApproaching.Low');
            }
            // General fallback
            return t('notificationDescriptions.TaskDeadlineApproaching.General');
        }
        if (notificationType === TaskNotificationType.TaskCompleted) {
            return t('notificationDescriptions.TaskCompleted');
        }
        // Activity notifications
        if (notificationType === ActivityNotificationType.BirthdayCelebration) {
            return t('notificationDescriptions.BirthdayCelebration');
        }
        if (notificationType === ActivityNotificationType.WorkAnniversary) {
            return t('notificationDescriptions.WorkAnniversary');
        }
        if (notificationType === ActivityNotificationType.WeddingAnniversary) {
            return t('notificationDescriptions.WeddingAnniversary');
        }
        if (notificationType === ActivityNotificationType.BirthAdoption) {
            return t('notificationDescriptions.BirthAdoption');
        }
        if (notificationType === ActivityNotificationType.Retirement) {
            return t('notificationDescriptions.Retirement');
        }
        if (notificationType === ActivityNotificationType.MarriageRegistration) {
            return t('notificationDescriptions.MarriageRegistration');
        }
        if (notificationType === ActivityNotificationType.EmployeeOnboarding) {
            return t('notificationDescriptions.EmployeeOnboarding');
        }
        if (notificationType === ActivityNotificationType.EmployeeDeparture) {
            return t('notificationDescriptions.EmployeeDeparture');
        }
        if (notificationType === ActivityNotificationType.EmployeeDeath) {
            return t('notificationDescriptions.EmployeeDeath');
        }
        if (notificationType === ActivityNotificationType.EmployeeIllness) {
            return t('notificationDescriptions.EmployeeIllness');
        }
        if (notificationType === ActivityNotificationType.CompanyAnniversary) {
            return t('notificationDescriptions.CompanyAnniversary');
        }
        // Default fallback
        return '';
    };
    // Get preferences from Redux store
    const eventsPreferences = useAppSelector((state) => state.notificationPreferences.preferencesByModule[NotificationModule.Events]);
    const tasksPreferences = useAppSelector((state) => state.notificationPreferences.preferencesByModule[NotificationModule.Tasks]);
    const activitiesPreferences = useAppSelector((state) => state.notificationPreferences.preferencesByModule[NotificationModule.Activities]);
    const moduleCompanies = useAppSelector((state) => state.notificationPreferences.moduleCompaniesByModule[NotificationModule.Activities]);
    const companies = useAppSelector((state) => state.company.companies);
    const companiesByRegion = useAppSelector((state) => state.company.companiesByRegion);
    // Company region lookup for adding regionLabel to options
    const companyRegionLookup = useMemo(() => {
        const lookup = {};
        companiesByRegion.forEach((region) => {
            region.companies.forEach((company) => {
                lookup[company.id] = region.regionLabel || 'Other';
            });
        });
        return lookup;
    }, [companiesByRegion]);
    // Load preferences on mount
    useEffect(() => {
        dispatch(getNotificationPreferencesByModuleAsync(NotificationModule.Events));
        dispatch(getNotificationPreferencesByModuleAsync(NotificationModule.Tasks));
        dispatch(getNotificationPreferencesByModuleAsync(NotificationModule.Activities));
        dispatch(getModuleCompaniesAsync(NotificationModule.Activities));
    }, [dispatch]);
    // Map API data to categories
    const categories = useMemo(() => {
        const allPreferences = [...eventsPreferences, ...tasksPreferences, ...activitiesPreferences];
        return [
            mapPreferencesToCategories(allPreferences, NotificationModule.Events, _jsx(MagicStar, { size: 24, color: theme.palette.action.disabled, variant: 'Outline' }), t('settings.events'), t),
            mapPreferencesToCategories(allPreferences, NotificationModule.Tasks, _jsx(Task, { size: 24, variant: 'Outline', color: theme.palette.action.disabled }), t('settings.tasks'), t),
            mapPreferencesToCategories(allPreferences, NotificationModule.Activities, _jsx(UserOctagon, { size: 24, variant: 'Outline', color: theme.palette.action.disabled }), t('settings.people'), t)
        ];
    }, [eventsPreferences, tasksPreferences, activitiesPreferences, theme, t]);
    // People options for Activities module
    const peopleOptions = [
        { value: 'birthday', label: t('settings.peopleBirthday') },
        { value: 'anniversary', label: t('settings.peopleAnniversary') },
        { value: 'new-contact', label: t('settings.peopleNewContact') },
        { value: 'update', label: t('settings.peopleUpdate') }
    ];
    const handleAccordionChange = (categoryId) => {
        setExpanded((prev) => prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]);
    };
    const handleMasterToggle = async (categoryId, enabled) => {
        const category = categories.find((cat) => cat.id === categoryId);
        if (!category)
            return;
        // Update all settings in the category
        for (const setting of category.settings) {
            await handleSettingToggle(categoryId, setting.id, enabled, true);
        }
    };
    const handleSettingToggle = async (categoryId, settingId, enabled, isMasterToggle = false) => {
        const category = categories.find((cat) => cat.id === categoryId);
        if (!category)
            return;
        const setting = category.settings.find((s) => s.id === settingId);
        if (!setting || !setting.notificationType)
            return;
        // Debug: Log the setting details for Task Deadline Approaching
        if (categoryId === 'tasks' &&
            setting.notificationType === TaskNotificationType.TaskDeadlineApproaching) {
            console.log('Toggling Task Deadline Approaching:', {
                settingId,
                taskPriority: setting.taskPriority,
                enabled,
                preferenceId: setting.preferenceId
            });
        }
        // Get module from category
        const moduleMap = {
            events: NotificationModule.Events,
            tasks: NotificationModule.Tasks,
            activities: NotificationModule.Activities
        };
        const module = moduleMap[categoryId];
        if (!module)
            return;
        try {
            const currentReminderValues = getValues(`reminder-${categoryId}-${settingId}`) || setting.reminderValues || [];
            const effectiveCompanies = module === NotificationModule.Activities
                ? getValues('activities-companies') ||
                    moduleCompanies?.companyIds ||
                    setting.companyIds ||
                    []
                : setting.companyIds || [];
            // Activities require at least one company when enabling
            if (module === NotificationModule.Activities &&
                enabled &&
                (!effectiveCompanies || effectiveCompanies.length === 0)) {
                setCompanyError(t('settings.companiesRequired') || 'Please select at least one company.');
                return;
            }
            else if (module === NotificationModule.Activities) {
                setCompanyError(undefined);
            }
            if (setting.preferenceId) {
                // Update existing preference
                const updateCommand = {
                    id: setting.preferenceId,
                    isEnabled: enabled,
                    taskPriority: setting.taskPriority ?? null,
                    companyIds: effectiveCompanies ?? [],
                    designations: setting.designations ?? null,
                    timingSettings: setting.hasReminder && currentReminderValues && currentReminderValues.length > 0
                        ? currentReminderValues.map((rv, idx) => ({
                            notificationPreferenceId: setting.preferenceId,
                            priorityType: module === NotificationModule.Tasks ? (setting.taskPriority ?? null) : null,
                            ...parseTimingString(rv),
                            orderIndex: idx
                        }))
                        : []
                };
                await dispatch(updateNotificationPreferenceAsync(updateCommand)).unwrap();
            }
            else {
                // Create new preference
                const createCommand = {
                    module,
                    notificationType: setting.notificationType,
                    isEnabled: enabled,
                    taskPriority: setting.taskPriority ?? null,
                    companyIds: effectiveCompanies ?? [],
                    designations: setting.designations ?? null,
                    timingSettings: setting.hasReminder && currentReminderValues && currentReminderValues.length > 0
                        ? currentReminderValues.map((rv, idx) => ({
                            priorityType: module === NotificationModule.Tasks ? (setting.taskPriority ?? null) : null,
                            ...parseTimingString(rv),
                            orderIndex: idx
                        }))
                        : []
                };
                await dispatch(createNotificationPreferenceAsync(createCommand)).unwrap();
            }
            // Reload preferences for the module
            await dispatch(getNotificationPreferencesByModuleAsync(module));
        }
        catch (error) {
            console.error('Failed to update notification preference:', error);
        }
    };
    const reminderOptions = [
        { value: 'On the day', label: t('settings.reminderOnDay') },
        { value: '1 day', label: t('settings.reminder1Day') },
        { value: '2 days', label: t('settings.reminder2Days') },
        { value: '3 days', label: t('settings.reminder3Days') },
        { value: '4 days', label: t('settings.reminder4Days') },
        { value: '5 days', label: t('settings.reminder5Days') },
        { value: '6 days', label: t('settings.reminder6Days') },
        { value: '1 week', label: t('settings.reminder1Week') },
        { value: '2 weeks', label: t('settings.reminder2Weeks') },
        { value: '3 weeks', label: t('settings.reminder3Weeks') },
        { value: '1 month', label: t('settings.reminder1Month') },
        { value: '2 months', label: t('settings.reminder2Months') },
        { value: '3 months', label: t('settings.reminder3Months') }
    ];
    // Company options for checkbox autocomplete (with string values for OptionType)
    const companyCheckboxOptions = useMemo(() => (companies || []).map((c) => ({
        label: c.name ?? '',
        value: String(c.id),
        regionLabel: companyRegionLookup[c.id] || 'Other'
    })), [companies, companyRegionLookup]);
    // Region groups for company selector
    const regionGroups = useMemo(() => getCompanyRegionGroups(companyCheckboxOptions), [companyCheckboxOptions]);
    // Create form for reminder values and company selections
    const { control, watch, reset, getValues } = useForm({
        defaultValues: {}
    });
    // Initialize form values from categories
    useEffect(() => {
        const formValues = {};
        categories.forEach((cat) => {
            cat.settings.forEach((setting) => {
                if (setting.hasReminder) {
                    formValues[`reminder-${cat.id}-${setting.id}`] = setting.reminderValues || [];
                }
            });
            if (cat.id === 'activities') {
                const first = cat.settings[0];
                const initialCompanies = moduleCompanies?.companyIds?.length
                    ? moduleCompanies.companyIds
                    : first?.companyIds?.length
                        ? first.companyIds
                        : [];
                formValues['activities-companies'] = initialCompanies;
                setCompanyError(undefined);
            }
        });
        reset(formValues);
    }, [categories, reset, moduleCompanies]);
    // Watch for changes and update API (only the changed field)
    useEffect(() => {
        const subscription = watch(async (value, { name }) => {
            if (!name)
                return;
            if (name.startsWith('reminder-')) {
                const parts = name.replace('reminder-', '').split('-');
                if (parts.length < 2)
                    return;
                const categoryId = parts[0];
                const settingId = parts.slice(1).join('-');
                const reminderValues = value[name];
                if (!Array.isArray(reminderValues))
                    return;
                const category = categories.find((cat) => cat.id === categoryId);
                if (!category)
                    return;
                const setting = category.settings.find((s) => s.id === settingId);
                if (!setting || !setting.notificationType || !setting.preferenceId)
                    return;
                const moduleMap = {
                    events: NotificationModule.Events,
                    tasks: NotificationModule.Tasks,
                    activities: NotificationModule.Activities
                };
                const module = moduleMap[categoryId];
                if (!module)
                    return;
                try {
                    // For activities, ensure at least one company selected before update
                    if (module === NotificationModule.Activities) {
                        const companiesSelected = value['activities-companies'] || moduleCompanies?.companyIds || [];
                        if (!companiesSelected || companiesSelected.length === 0) {
                            setCompanyError(t('settings.companiesRequired') || '');
                            return;
                        }
                        else {
                            setCompanyError(undefined);
                        }
                        // Persist module-level companies even if no notifications yet
                        await dispatch(updateModuleCompaniesAsync({
                            module: NotificationModule.Activities,
                            companyIds: companiesSelected,
                            designations: []
                        })).unwrap();
                        await dispatch(getModuleCompaniesAsync(NotificationModule.Activities));
                    }
                    const updateCommand = {
                        id: setting.preferenceId,
                        isEnabled: setting.enabled,
                        taskPriority: setting.taskPriority ?? null,
                        companyIds: setting.companyIds ?? [],
                        designations: setting.designations ?? null,
                        timingSettings: reminderValues.map((rv, idx) => ({
                            notificationPreferenceId: setting.preferenceId,
                            priorityType: module === NotificationModule.Tasks ? (setting.taskPriority ?? null) : null,
                            ...parseTimingString(rv),
                            orderIndex: idx
                        }))
                    };
                    await dispatch(updateNotificationPreferenceAsync(updateCommand)).unwrap();
                    await dispatch(getNotificationPreferencesByModuleAsync(module));
                }
                catch (error) {
                    console.error('Failed to update timing setting:', error);
                }
            }
            if (name === 'activities-companies') {
                const selectedCompanies = value[name];
                const activitiesCategory = categories.find((c) => c.id === 'activities');
                if (!activitiesCategory)
                    return;
                const module = NotificationModule.Activities;
                // Allow empty to persist clearing; show error but still save
                if (!selectedCompanies || selectedCompanies.length === 0) {
                    setCompanyError(t('settings.companiesRequired') || '');
                }
                else {
                    setCompanyError(undefined);
                }
                try {
                    await dispatch(updateModuleCompaniesAsync({
                        module,
                        companyIds: selectedCompanies,
                        designations: []
                    })).unwrap();
                    await dispatch(getModuleCompaniesAsync(module));
                    // Optimistically reflect in UI: if no companies, disable all activity prefs locally; otherwise update companyIds
                    if (!selectedCompanies || selectedCompanies.length === 0) {
                        const disabledPrefs = activitiesPreferences.map((p) => ({
                            ...p,
                            isEnabled: false,
                            companyIds: [],
                            timingSettings: []
                        }));
                        dispatch(setPreferencesByModule({ module, preferences: disabledPrefs }));
                    }
                    else {
                        const updatedPrefs = activitiesPreferences.map((p) => ({
                            ...p,
                            companyIds: selectedCompanies
                        }));
                        dispatch(setPreferencesByModule({ module, preferences: updatedPrefs }));
                    }
                    await dispatch(getNotificationPreferencesByModuleAsync(module));
                }
                catch (error) {
                    console.error('Failed to update companies:', error);
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, categories, dispatch, activitiesPreferences, moduleCompanies?.companyIds, t]);
    const handlePeopleMenuOpen = (event) => {
        event.stopPropagation();
        setPeopleMenuAnchor(event.currentTarget);
    };
    const handlePeopleMenuClose = () => {
        setPeopleMenuAnchor(null);
    };
    const handlePeopleOptionToggle = (value) => {
        setSelectedPeopleOptions((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
    };
    const getPriorityIcon = (settingId) => {
        if (settingId.includes('most-important')) {
            return _jsx(Warning2, { variant: 'Bulk', color: '#22C55E', size: 20 });
        }
        else if (settingId.includes('high')) {
            return _jsx(Warning2, { variant: 'Bulk', color: '#FF5630', size: 20 });
        }
        else if (settingId.includes('medium')) {
            return _jsx(Warning2, { variant: 'Bulk', color: '#FFAB00', size: 20 });
        }
        else if (settingId.includes('low')) {
            return _jsx(Warning2, { variant: 'Bulk', color: '#637381', size: 20 });
        }
        return null;
    };
    const getPeopleSettingIcon = (settingId, label) => {
        if (label.toLowerCase().includes('like')) {
            return _jsx(Like1, { variant: 'Bulk', color: theme.palette.info.main, size: 20 });
        }
        else if (label.toLowerCase().includes('birthday')) {
            return _jsx(Cake, { variant: 'Bulk', color: theme.palette.warning.main, size: 20 });
        }
        else if (label.toLowerCase().includes('anniversary')) {
            return _jsx(CalendarTick, { variant: 'Bulk', color: theme.palette.success.main, size: 20 });
        }
        return null;
    };
    const filteredCategories = categories.filter((cat) => cat.title.toLowerCase().includes(searchValue.toLowerCase()) ||
        cat.settings.some((s) => s.label.toLowerCase().includes(searchValue.toLowerCase())));
    return (_jsx(Box, { sx: { overflowY: 'auto' }, children: _jsxs(Box, { sx: {
                overflowY: 'auto',
                height: isSmallScreen
                    ? isMobileScreen
                        ? 'calc(100dvh - 250px)'
                        : 'calc(100dvh - 300px)'
                    : 'calc(100dvh - 136px)'
            }, children: [_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: filteredCategories.map((category) => (_jsxs(Accordion, { expanded: expanded.includes(category.id), onChange: () => handleAccordionChange(category.id), sx: {
                            boxShadow: 'none',
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                            borderRadius: '14px !important',
                            overflow: 'hidden',
                            '&:before': {
                                display: 'none'
                            },
                            '&.Mui-expanded': {
                                margin: 0
                            },
                            '&.MuiPaper-root-MuiAccordion-root': {
                                borderRadius: '14px !important'
                            }
                        }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), sx: {
                                    backgroundColor: theme.palette.background.paper,
                                    borderRadius: '14px',
                                    px: 2.5,
                                    py: 1.5,
                                    '&.Mui-expanded': {
                                        borderBottomLeftRadius: 0,
                                        borderBottomRightRadius: 0,
                                        py: 0
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        alignItems: 'center',
                                        my: 0
                                    }
                                }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flex: 1 }, children: [_jsx(Box, { sx: { color: theme.palette.primary.main }, children: category.icon }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { variant: 'h6', sx: { fontWeight: 500 }, children: category.title }), _jsxs(Typography, { variant: 'body2', color: 'text.secondary', children: [category.enabledCount, " ", t('settings.of'), " ", category.totalCount, ' ', t('settings.notifications')] })] }), _jsx(FormControlLabel, { control: _jsx(Switch, { checked: category.enabled, onChange: (e) => handleMasterToggle(category.id, e.target.checked), color: 'primary', onClick: (e) => e.stopPropagation() }), label: '', sx: { m: 0 } })] }) }), _jsx(AccordionDetails, { sx: {
                                    backgroundColor: theme.palette.background.paper,
                                    borderRadius: 1,
                                    px: 2.5,
                                    py: 2
                                }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [category.id === 'activities' && (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }, children: _jsx(Controller, { name: 'activities-companies', control: control, render: ({ field }) => {
                                                    // Convert form value (array of string values) to OptionType[] for display
                                                    const fieldValue = field.value || [];
                                                    const selectedOptions = Array.isArray(fieldValue)
                                                        ? companyCheckboxOptions.filter((opt) => fieldValue.some((val) => String(val) === opt.value))
                                                        : [];
                                                    return (_jsx(HCheckboxAutoComplete, { value: selectedOptions, onChange: (newOptions) => {
                                                            // Convert OptionType[] back to array of string values for form
                                                            const valuesArray = newOptions.map((opt) => opt.value);
                                                            field.onChange(valuesArray);
                                                        }, options: companyCheckboxOptions, label: t('settings.companies') || 'Companies', bg: 'grey8', maxChips: 5, error: companyError, showSelectAll: true, selectAllLabel: t('actions.selectAll'), isSortable: false, fullWidth: true, groupBy: (option) => option.regionLabel || '', renderGroup: (params) => {
                                                            const regionLabel = params.group;
                                                            const regionCompanyIds = regionGroups[regionLabel] || [];
                                                            const currentCompanyIds = selectedOptions.map((opt) => opt.value);
                                                            const allSelected = regionCompanyIds.length > 0 &&
                                                                regionCompanyIds.every((id) => currentCompanyIds.includes(id));
                                                            const someSelected = !allSelected &&
                                                                regionCompanyIds.some((id) => currentCompanyIds.includes(id));
                                                            const isUngrouped = !regionLabel;
                                                            return (_jsxs("li", { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', sx: { pr: 1 }, onClick: (e) => e.stopPropagation(), children: [_jsx(Typography, { variant: 'subtitle2', sx: { pl: 2, pt: 1, pb: 0.5, color: 'text.secondary' }, children: regionLabel }), !isUngrouped && (_jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, onClick: (e) => {
                                                                                    e.stopPropagation();
                                                                                    if (allSelected) {
                                                                                        const updatedSelection = selectedOptions.filter((opt) => !regionCompanyIds.includes(opt.value));
                                                                                        field.onChange(updatedSelection.map((opt) => opt.value));
                                                                                    }
                                                                                    else {
                                                                                        const newCompanies = companyCheckboxOptions.filter((opt) => regionCompanyIds.includes(opt.value) &&
                                                                                            !currentCompanyIds.includes(opt.value));
                                                                                        const updatedSelection = [
                                                                                            ...selectedOptions,
                                                                                            ...newCompanies
                                                                                        ];
                                                                                        field.onChange(updatedSelection.map((opt) => opt.value));
                                                                                    }
                                                                                }, sx: {
                                                                                    '&.Mui-disabled': {
                                                                                        opacity: 0.5
                                                                                    }
                                                                                } }))] }), _jsx("ul", { style: { padding: 0, margin: 0 }, children: params.children })] }, params.key));
                                                        } }));
                                                } }) })), category.settings.map((setting, index) => {
                                            const isTaskDeadline = category.id === 'tasks' &&
                                                setting.notificationType === TaskNotificationType.TaskDeadlineApproaching;
                                            const isFirstTaskDeadline = isTaskDeadline &&
                                                (index === 0 ||
                                                    category.settings[index - 1].notificationType !==
                                                        TaskNotificationType.TaskDeadlineApproaching);
                                            const shouldShowParent = isFirstTaskDeadline;
                                            return (_jsxs(React.Fragment, { children: [shouldShowParent && (_jsx(Box, { sx: {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            p: 1.5,
                                                            backgroundColor: theme.palette.background.neutral,
                                                            borderRadius: 1.75
                                                        }, children: _jsx(Typography, { variant: 'body2', sx: { fontWeight: 500 }, children: t('settings.taskDeadlineApproaching') }) })), _jsx(Box, { sx: {
                                                            display: 'flex',
                                                            flexDirection: isSmallScreen ? 'row' : 'column',
                                                            backgroundColor: theme.palette.common.grey8,
                                                            borderRadius: 1.75,
                                                            justifyContent: isSmallScreen ? 'space-between' : 'flex-start',
                                                            gap: 1,
                                                            p: 1.5,
                                                            marginLeft: isTaskDeadline ? 1.5 : 0,
                                                            '&:last-child': {
                                                                borderBottom: 'none'
                                                            }
                                                        }, children: _jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1,
                                                                width: '100%'
                                                            }, children: [_jsxs(Box, { sx: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        gap: 2,
                                                                        width: '100%'
                                                                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flex: 1 }, children: [setting.id.includes('priority') &&
                                                                                    category.id !== 'people' &&
                                                                                    getPriorityIcon(setting.id), category.id === 'people' &&
                                                                                    getPeopleSettingIcon(setting.id, setting.label), _jsx(Typography, { variant: 'body2', sx: { fontWeight: 400 }, children: isTaskDeadline
                                                                                        ? setting.label
                                                                                            .replace('Task Deadline Approaching (', '')
                                                                                            .replace(')', '')
                                                                                        : setting.label }), _jsx(Tooltip, { title: getNotificationDescription(setting.notificationType, setting.taskPriority), placement: 'bottom', children: _jsx(Icon, { icon: 'material-symbols:info-rounded', color: theme.palette.primary.main, fontSize: 24, style: { cursor: 'pointer' } }) })] }), _jsxs(Box, { sx: {
                                                                                display: { xs: 'none', sm: 'flex' },
                                                                                alignItems: 'center',
                                                                                justifyContent: 'flex-end',
                                                                                gap: 2,
                                                                                flexShrink: 0
                                                                            }, children: [setting.hasReminder && (_jsx(Box, { sx: { minWidth: 240 }, children: _jsx(Box, { sx: { minWidth: 200, width: '100%' }, children: _jsx(ControlledModernHCheckboxAutocomplete, { control: control, name: `reminder-${category.id}-${setting.id}`, label: t('settings.remindBefore'), options: reminderOptions, bg: 'light', selectedCountLabel: t('settings.selected') }) }) })), category.id === 'people' &&
                                                                                    setting.id === 'people-notification' && (_jsxs(Box, { onClick: (e) => handlePeopleMenuOpen(e), sx: {
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'space-between',
                                                                                        minWidth: 240,
                                                                                        px: 2,
                                                                                        py: 1.25,
                                                                                        backgroundColor: theme.palette.background.paper,
                                                                                        borderRadius: '8px',
                                                                                        cursor: 'pointer',
                                                                                        border: 'none',
                                                                                        '&:hover': {
                                                                                            backgroundColor: theme.palette.action.hover
                                                                                        }
                                                                                    }, children: [_jsx(Typography, { variant: 'body2', sx: {
                                                                                                color: selectedPeopleOptions.length > 0
                                                                                                    ? theme.palette.text.primary
                                                                                                    : theme.palette.text.secondary,
                                                                                                fontSize: '14px',
                                                                                                fontWeight: 400
                                                                                            }, children: selectedPeopleOptions.length > 0
                                                                                                ? `${selectedPeopleOptions.length} ${selectedPeopleOptions.length === 1 ? t('settings.option') : t('settings.options')} ${t('settings.selected')}`
                                                                                                : t('settings.selectPeopleOptions') || 'Select options' }), _jsx(KeyboardArrowDown, { sx: {
                                                                                                fontSize: 20,
                                                                                                color: theme.palette.text.secondary,
                                                                                                ml: 1
                                                                                            } })] })), _jsx(FormControlLabel, { control: _jsx(Switch, { checked: setting.enabled, onChange: (e) => handleSettingToggle(category.id, setting.id, e.target.checked), color: 'primary', onClick: (e) => e.stopPropagation() }), label: '', sx: { m: 0, flexShrink: 0 } })] }), _jsx(Box, { sx: {
                                                                                display: { xs: 'flex', sm: 'none' },
                                                                                flexShrink: 0
                                                                            }, children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: setting.enabled, onChange: (e) => handleSettingToggle(category.id, setting.id, e.target.checked), color: 'primary', onClick: (e) => e.stopPropagation() }), label: '', sx: { m: 0 } }) })] }), setting.description && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', sx: { display: 'block' }, children: setting.description })), _jsxs(Box, { sx: {
                                                                        display: { xs: 'flex', sm: 'none' },
                                                                        flexDirection: 'column'
                                                                    }, children: [category.id === 'people' && setting.id === 'people-notification' && (_jsxs(Box, { onClick: (e) => handlePeopleMenuOpen(e), sx: {
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'space-between',
                                                                                minWidth: 200,
                                                                                width: '100%',
                                                                                px: 2,
                                                                                py: 1.25,
                                                                                backgroundColor: theme.palette.background.paper,
                                                                                borderRadius: '8px',
                                                                                cursor: 'pointer',
                                                                                border: 'none',
                                                                                '&:hover': {
                                                                                    backgroundColor: theme.palette.action.hover
                                                                                }
                                                                            }, children: [_jsx(Typography, { variant: 'body2', sx: {
                                                                                        color: selectedPeopleOptions.length > 0
                                                                                            ? theme.palette.text.primary
                                                                                            : theme.palette.text.secondary,
                                                                                        fontSize: '14px',
                                                                                        fontWeight: 400
                                                                                    }, children: selectedPeopleOptions.length > 0
                                                                                        ? `${selectedPeopleOptions.length} ${selectedPeopleOptions.length === 1 ? t('settings.option') : t('settings.options')} ${t('settings.selected')}`
                                                                                        : t('settings.selectPeopleOptions') || 'Select options' }), _jsx(KeyboardArrowDown, { sx: {
                                                                                        fontSize: 20,
                                                                                        color: theme.palette.text.secondary,
                                                                                        ml: 1
                                                                                    } })] })), setting.hasReminder && (_jsx(Box, { sx: { minWidth: 200, width: '100%' }, children: _jsx(ControlledModernHCheckboxAutocomplete, { control: control, name: `reminder-${category.id}-${setting.id}`, label: t('settings.remindBefore'), options: reminderOptions, bg: 'light', selectedCountLabel: t('settings.selected') }) }))] }), category.id === 'people' &&
                                                                    setting.id === 'people-notification' &&
                                                                    selectedPeopleOptions.length > 0 && (_jsx(Box, { sx: {
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        gap: 1,
                                                                        justifyContent: 'flex-start',
                                                                        pt: 1
                                                                    }, children: selectedPeopleOptions.map((value) => {
                                                                        const option = peopleOptions.find((opt) => opt.value === value);
                                                                        return option ? (_jsx(Chip, { label: option.label, size: 'small', variant: 'soft', color: 'default', onDelete: () => handlePeopleOptionToggle(value), sx: {
                                                                                height: 28,
                                                                                fontSize: '13px'
                                                                            } }, value)) : null;
                                                                    }) }))] }) }, setting.id)] }, setting.id));
                                        })] }) })] }, category.id))) }), _jsx(Menu, { anchorEl: peopleMenuAnchor, open: Boolean(peopleMenuAnchor), onClose: handlePeopleMenuClose, anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                    }, transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                    }, PaperProps: {
                        sx: {
                            mt: 1,
                            ml: 1,
                            minWidth: isMobile ? '100%' : 200,
                            maxWidth: isMobile ? '90vw' : 'none',
                            borderRadius: '8px',
                            backgroundColor: '#2E3440',
                            color: theme.palette.common.white,
                            boxShadow: '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D',
                            border: 'none'
                        }
                    }, children: peopleOptions.map((option) => (_jsxs(MenuItem, { onClick: () => handlePeopleOptionToggle(option.value), sx: {
                            py: 1,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: theme.palette.common.white,
                            '&:hover': {
                                backgroundColor: 'rgba(21, 10, 10, 0.08)'
                            }
                        }, children: [_jsx(Checkbox, { checked: selectedPeopleOptions.includes(option.value), size: 'small', sx: {
                                    mr: 1,
                                    color: theme.palette.common.white,
                                    '&.Mui-checked': {
                                        color: theme.palette.common.white
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme.palette.common.white
                                    }
                                } }), _jsx(Typography, { sx: {
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: theme.palette.common.white
                                }, children: option.label })] }, option.value))) })] }) }));
};
export default NotificationsTab;
